import axios from "axios";

/**
 * Session expirée ou jeton refusé (JWT signé par une clé qui n'est plus acceptée, jeton expiré) :
 * sur un 401 d'une requête AUTHENTIFIÉE (en-tête « Authorization: Bearer »), hors routes
 * d'authentification, on purge la session locale et on renvoie UNE FOIS vers la connexion.
 *
 * - les 401 des routes d'authentification (mauvais identifiants, lien expiré…) et les requêtes
 *   anonymes ne déclenchent rien : l'écran concerné affiche son propre message ;
 * - les autres erreurs (403, 404, 5xx, réseau) ne déclenchent rien ;
 * - une requête marquée `skipSessionExpiry: true` (appel de fond non essentiel) ne déclenche rien ;
 * - pas de redirection depuis une page d'authentification (pas de boucle), et une seule
 *   redirection même si plusieurs requêtes échouent en même temps.
 *
 * Installé en tout premier import de index.js : couvre l'axios par défaut ET chaque instance
 * créée ensuite par axios.create (les services créent leurs instances au chargement).
 */

/** Requêtes d'authentification : un 401 y signifie « identifiants refusés », pas « session expirée ». */
export const AUTH_REQUEST = /\/auth\/(login|googleWithVerification|set-password)\b|\/password-setup\//;

const bearerOf = (headers) => {
    if (!headers) return null;
    if (typeof headers.get === "function") return headers.get("Authorization") || headers.get("authorization");
    return headers.Authorization || headers.authorization || null;
};

export const isSessionExpiry = (error) => {
    if (error?.response?.status !== 401) return false;
    const config = error.config || {};
    if (AUTH_REQUEST.test(config.url || "")) return false;
    // Appel de fond vers un autre service (ex. alerte documents) : son 401 ne prouve pas que la session a expiré.
    if (config.skipSessionExpiry === true) return false;
    const auth = bearerOf(config.headers);
    return typeof auth === "string" && auth.startsWith("Bearer ");
};

let redirecting = false;

/** Pour les tests uniquement. */
export const resetSessionExpiryState = () => {
    redirecting = false;
};

export const createSessionExpiryHandler = ({ sessionKeys, purgePersistedAuth, loginPath, authPages, env }) => () => {
    const storage = env?.storage ?? window.localStorage;
    const location = env?.location ?? window.location;
    sessionKeys.forEach((key) => {
        try {
            storage.removeItem(key);
        } catch {
            // stockage indisponible : la redirection suffit
        }
    });
    if (purgePersistedAuth) purgePersistedAuth(storage);
    const onAuthPage = authPages.some((page) => location.pathname === page || location.pathname.startsWith(`${page}/`));
    if (redirecting || onAuthPage) return false;
    redirecting = true;
    location.assign(`${loginPath}?session=expired`);
    return true;
};

export const attachSessionExpiry = (instance, onExpiry) => {
    instance.interceptors.response.use(
        (response) => response,
        (error) => {
            if (isSessionExpiry(error)) onExpiry();
            return Promise.reject(error);
        },
    );
    return instance;
};

/** Idempotent. */
export const installSessionExpiry = (onExpiry, ax = axios) => {
    if (ax.__sessionExpiryInstalled) return;
    ax.__sessionExpiryInstalled = true;
    attachSessionExpiry(ax, onExpiry);
    const create = ax.create.bind(ax);
    ax.create = (...args) => attachSessionExpiry(create(...args), onExpiry);
};
